/**
 * FinecomPage — Revenue-Share Abrechnung mit Finecom (Outbound + Inbound/Retouren + Lager).
 *
 * 3 Tabs: Abrechnungen | Kunden | Einstellungen
 * Workflow: Draft → Approve → Send (mit PDF)
 *
 * @page /finecom
 * @roles administrator, ops_manager, controlling
 * @since v2.163.0
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Receipt, Plus, Check, Send, FileText, Trash2, RefreshCw, ChevronDown, ChevronUp, X, UserPlus, Settings, Upload, AlertCircle, CheckCircle, TrendingUp, TrendingDown, BarChart3, AlertTriangle, RotateCcw, MessageSquare, Clock, ArrowUpDown, Info } from 'lucide-react';
import useTabParam from '../hooks/useTabParam';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, LineChart, Legend } from 'recharts';

const api = (path, opts) => fetch(path, { credentials: 'include', ...opts }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
const apiPost = (path, body) => api(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const apiPut = (path, body) => api(path, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const apiDel = (path) => fetch(path, { credentials: 'include', method: 'DELETE' }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
const fmt = (n) => Number(n || 0).toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Normalize a monat value (DATE from pg, may have TZ shift) to YYYY-MM-01 */
const normalizeMonat = (m) => {
  if (!m) return '';
  const s = typeof m === 'string' ? m : '';
  // pg DATE comes as "2026-02-28T23:00:00.000Z" (UTC shift from 2026-03-01)
  // Parse via Date and use local year/month to get correct first-of-month
  const d = new Date(s || m);
  if (isNaN(d.getTime())) return s.substring(0, 10);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

// ─── Custom Confirm/Alert Modal (replaces window.confirm / window.alert) ───
function ConfirmModal({ open, title, message, confirmLabel, confirmColor, icon: Icon, onConfirm, onCancel, t }) {
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} onClick={onCancel}>
      <div style={{ background: 'var(--bg-card,#fff)', borderRadius: 16, padding: 24, width: 440, border: '1px solid var(--border-card,#E5E7EB)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          {Icon && <div style={{ width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: confirmColor === '#EF4444' ? '#FEE2E2' : '#EBF4FD' }}>
            <Icon size={20} style={{ color: confirmColor || '#338edc' }} />
          </div>}
          <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{title}</h3>
        </div>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onCancel} style={{ padding: '10px 20px', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 10, background: 'transparent', fontSize: 14, cursor: 'pointer', color: 'var(--text-primary)' }}>{t('finecom.cancel')}</button>
          <button onClick={onConfirm} style={{ padding: '10px 20px', background: confirmColor || '#338edc', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function AlertModal({ open, title, message, onClose }) {
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} onClick={onClose}>
      <div style={{ background: 'var(--bg-card,#fff)', borderRadius: 16, padding: 24, width: 440, border: '1px solid var(--border-card,#E5E7EB)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FEF3C7' }}>
            <AlertTriangle size={20} style={{ color: '#D97706' }} />
          </div>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{title || 'Hinweis'}</h3>
        </div>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '10px 20px', background: '#338edc', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>OK</button>
        </div>
      </div>
    </div>
  );
}

export default function FinecomPage() {
  const { t } = useLanguage();
  const { user } = useAuth();

  // Permissions (#983) — Read/Edit/Settings granular
  const canEdit = (user?.permissions?.finecom_edit || 'none') !== 'none';
  const canSettings = (user?.permissions?.finecom_settings || 'none') !== 'none';

  const [tab, setTab] = useTabParam('abrechnungen');
  const [abrechnungen, setAbrechnungen] = useState([]);
  const [kunden, setKunden] = useState([]);
  const [revshare, setRevshare] = useState([]);
  const [config, setConfig] = useState({});
  const [alleKunden, setAlleKunden] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [genMonat, setGenMonat] = useState('');
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [showAddKunde, setShowAddKunde] = useState(false);
  const [mehraufwand, setMehraufwand] = useState(null);

  // Confirm/Alert modal state
  const [confirmModal, setConfirmModal] = useState({ open: false });
  const [alertMsg, setAlertMsg] = useState({ open: false, title: '', message: '' });
  const confirmResolveRef = useRef(null);

  const showAlert = (message, title) => setAlertMsg({ open: true, title: title || 'Hinweis', message });
  const showConfirm = (message, { title, confirmLabel, confirmColor, icon } = {}) => {
    return new Promise(resolve => {
      confirmResolveRef.current = resolve;
      setConfirmModal({ open: true, title: title || t('finecom.confirm'), message, confirmLabel: confirmLabel || 'OK', confirmColor, icon });
    });
  };
  const handleConfirmOk = () => { setConfirmModal({ open: false }); confirmResolveRef.current?.(true); };
  const handleConfirmCancel = () => { setConfirmModal({ open: false }); confirmResolveRef.current?.(false); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [abr, ku, rs, cfg] = await Promise.all([
        api('/api/finecom/abrechnungen'),
        api('/api/finecom/kunden'),
        api('/api/finecom/revshare'),
        canSettings ? api('/api/finecom/config').catch(() => ({})) : Promise.resolve({}),
      ]);
      setAbrechnungen(abr);
      setKunden(ku);
      setRevshare(rs);
      setConfig(cfg);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [canSettings]);

  useEffect(() => { load(); }, [load]);

  // Default Vormonat for generate (YYYY-MM format)
  useEffect(() => {
    const now = new Date();
    const vm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    setGenMonat(`${vm.getFullYear()}-${String(vm.getMonth() + 1).padStart(2, '0')}`);
  }, []);

  // Months that already have an Abrechnung (for filtering in month picker)
  const existingMonths = useMemo(() => new Set(abrechnungen.map(a => normalizeMonat(a.monat).substring(0, 7))), [abrechnungen]);

  const handleGenerate = async () => {
    setBusy(true);
    try {
      const result = await apiPost('/api/finecom/abrechnungen/generate', { monat: genMonat });
      setShowGenerate(false);
      await load();
      const full = await api(`/api/finecom/abrechnungen/${result.id}`);
      setDetail(full);
    } catch (e) { showAlert(e.error || t('finecom.errorGenerating')); }
    setBusy(false);
  };

  const handleResetMehraufwand = async (id) => {
    const ok = await showConfirm(t('finecom.confirmResetMehraufwand'), { title: t('finecom.mehraufwandReset'), confirmLabel: t('finecom.delete'), confirmColor: '#EF4444', icon: Trash2 });
    if (!ok) return;
    setBusy(true);
    try {
      await apiDel(`/api/finecom/abrechnungen/${id}/mehraufwand`);
      await openDetail(id);
      await load();
    } catch (e) { showAlert(e.error || t('finecom.error')); }
    setBusy(false);
  };

  const handleMehraufwandSaved = async (id) => {
    await openDetail(id);
    await load();
  };

  const handleApprove = async (id) => {
    const ok = await showConfirm(t('finecom.confirmApprove'), { title: t('finecom.approve'), confirmLabel: t('finecom.approve'), icon: Check });
    if (!ok) return;
    setBusy(true);
    try {
      await apiPut(`/api/finecom/abrechnungen/${id}/approve`, {});
      if (detail?.id === id) {
        const full = await api(`/api/finecom/abrechnungen/${id}`);
        setDetail(full);
      }
      await load();
    } catch (e) {
      // #1027: Lager-Werte aus Odoo-Drafts → Confirm + Force-Retry
      if (e.error_code === 'lager_drafts') {
        const list = (e.draft_kunden || []).join(', ');
        const force = await showConfirm(
          (t('finecom.lagerDraftsConfirm') || 'Lager-Werte enthalten Odoo-Drafts (Kunden: {kunden}). Trotzdem genehmigen?').replace('{kunden}', list),
          { title: t('finecom.lagerDraftsTitle') || 'Lager: Drafts vorhanden', confirmLabel: t('finecom.approve'), icon: Check }
        );
        if (force) {
          try {
            await apiPut(`/api/finecom/abrechnungen/${id}/approve?force=true`, {});
            if (detail?.id === id) {
              const full = await api(`/api/finecom/abrechnungen/${id}`);
              setDetail(full);
            }
            await load();
          } catch (err2) { showAlert(err2.error || t('finecom.error')); }
        }
      } else {
        showAlert(e.error || t('finecom.error'));
      }
    }
    setBusy(false);
  };

  const handleSend = async (id) => {
    const ok = await showConfirm(t('finecom.confirmSend'), { title: t('finecom.send'), confirmLabel: t('finecom.send'), icon: Send });
    if (!ok) return;
    setBusy(true);
    try {
      await apiPost(`/api/finecom/abrechnungen/${id}/send`, {});
      if (detail?.id === id) {
        const full = await api(`/api/finecom/abrechnungen/${id}`);
        setDetail(full);
      }
      await load();
    } catch (e) { showAlert(e.error || t('finecom.error')); }
    setBusy(false);
  };

  const handleRevert = async (id) => {
    const ok = await showConfirm(t('finecom.revertConfirm') || 'Abrechnung zurück auf Entwurf setzen? Sie kann dann erneut bearbeitet werden.', { title: t('finecom.revert') || 'Zurück auf Entwurf', confirmLabel: t('finecom.revert') || 'Zurücksetzen', icon: RotateCcw });
    if (!ok) return;
    setBusy(true);
    try {
      await apiPut(`/api/finecom/abrechnungen/${id}/revert`, {});
      if (detail?.id === id) {
        const full = await api(`/api/finecom/abrechnungen/${id}`);
        setDetail(full);
      }
      await load();
    } catch (e) { showAlert(e.error || t('finecom.error')); }
    setBusy(false);
  };

  const handleSaveNotizen = async (id, text) => {
    try {
      await apiPut(`/api/finecom/abrechnungen/${id}/notizen`, { notizen: text });
      // Update local state
      setAbrechnungen(prev => prev.map(a => a.id === id ? { ...a, notizen: text } : a));
      if (detail?.id === id) setDetail(prev => ({ ...prev, notizen: text }));
    } catch (e) { showAlert(e.error || t('finecom.error')); }
  };

  const handleDelete = async (id, monatLabel) => {
    const msg = (t('finecom.confirmDeleteFull') || 'Abrechnung {monat} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.').replace('{monat}', monatLabel || id);
    const ok = await showConfirm(msg, { title: t('finecom.delete'), confirmLabel: t('finecom.delete'), confirmColor: '#EF4444', icon: Trash2 });
    if (!ok) return;
    try {
      await apiDel(`/api/finecom/abrechnungen/${id}?confirm=true`);
      if (detail?.id === id) setDetail(null);
      await load();
    } catch (e) { showAlert(e.error || t('finecom.error')); }
  };

  const handleDownloadPdf = (id) => {
    window.open(`/api/finecom/abrechnungen/${id}/pdf`, '_blank');
  };

  const openDetail = async (id) => {
    const [full, ma] = await Promise.all([
      api(`/api/finecom/abrechnungen/${id}`),
      api(`/api/finecom/abrechnungen/${id}/mehraufwand`).catch(() => ({ entries: [], stundensatz: 45 })),
    ]);
    setDetail(full);
    setMehraufwand(ma);
  };

  const statusBadge = (status) => {
    const styles = {
      draft: { bg: 'var(--bg-inset,#F8F9FC)', color: 'var(--text-tertiary)', label: t('finecom.statusDraft') },
      approved: { bg: '#EBF4FD', color: '#338edc', label: t('finecom.statusApproved') },
      sent: { bg: '#D1FAE5', color: '#059669', label: t('finecom.statusSent') },
    };
    const s = styles[status] || styles.draft;
    return <span style={{ padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 500, background: s.bg, color: s.color }}>{s.label}</span>;
  };

  const fmtMonat = (d) => {
    const nm = normalizeMonat(d);
    if (!nm) return '–';
    const dt = new Date(nm + 'T12:00:00');
    return dt.toLocaleDateString('de-AT', { month: 'long', year: 'numeric' });
  };

  const toggleExpand = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  // ─── Kunden Tab ───
  const loadAlleKunden = async () => {
    try {
      const all = await api('/api/kunden?aktiv=true');
      setAlleKunden(Array.isArray(all) ? all : (all.kunden || []));
    } catch(e) { console.error(e); }
  };

  const handleAddKunde = async (kundeId) => {
    try {
      await apiPost('/api/finecom/kunden', { kunde_id: kundeId });
      setShowAddKunde(false);
      await load();
    } catch (e) { showAlert(e.error || t('finecom.error')); }
  };

  const handleRemoveKunde = async (kundeId) => {
    const ok = await showConfirm(t('finecom.confirmRemoveKunde'), { title: t('finecom.remove'), confirmLabel: t('finecom.remove'), confirmColor: '#EF4444', icon: Trash2 });
    if (!ok) return;
    try {
      await apiDel(`/api/finecom/kunden/${kundeId}`);
      await load();
    } catch (e) { showAlert(e.error || t('finecom.error')); }
  };

  const handleSyncKunden = async () => {
    try {
      const preview = await api('/api/finecom/kunden/sync-preview');
      if (!preview.length) { showAlert(t('finecom.syncNoNew')); return; }
      const names = preview.map(k => k.name).join(', ');
      const ok = await showConfirm(`${preview.length} ${t('finecom.syncFound')}: ${names}`, { title: t('finecom.syncTitle'), confirmLabel: t('finecom.syncConfirm') });
      if (!ok) return;
      const result = await apiPost('/api/finecom/kunden/sync');
      showAlert(`${result.added} ${t('finecom.syncAdded')}`);
      await load();
    } catch (e) { showAlert(e.error || t('finecom.error')); }
  };

  // ─── Einstellungen Tab ───
  const handleSaveConfig = async () => {
    setBusy(true);
    try {
      await apiPut('/api/finecom/config', config);
    } catch (e) { showAlert(t('finecom.error')); }
    setBusy(false);
  };

  const handleSaveRevshare = async (id, fc, ws) => {
    try {
      await apiPut(`/api/finecom/revshare/${id}`, { finecom_anteil: fc, weship_anteil: ws });
      await load();
    } catch (e) { showAlert(e.error || t('finecom.error')); }
  };

  // ═══════════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════════
  const tabs = [
    { key: 'abrechnungen', label: t('finecom.tabAbrechnungen') },
    { key: 'statistiken', label: t('finecom.tabStatistiken') },
    { key: 'partner-marge', label: t('finecom.tabPartnerMarge') },
    { key: 'kunden', label: t('finecom.tabKunden') },
    canSettings ? { key: 'einstellungen', label: t('finecom.tabEinstellungen') } : null,
  ].filter(Boolean);

  return (
    <div style={{ padding: 32 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, color: 'var(--text-primary)' }}>
          {t('finecom.title')}
        </h1>
        {tab === 'abrechnungen' && canEdit && (
          <button onClick={() => setShowGenerate(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#338edc', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            <Plus size={16} /> {t('finecom.newAbrechnung')}
          </button>
        )}
      </div>

      {/* Tab Bar */}
      <div style={{ display: 'inline-flex', background: 'var(--bg-inset,#F8F9FC)', borderRadius: 10, padding: 3, border: '1px solid var(--border-card,#E5E7EB)', marginBottom: 24 }}>
        {tabs.map(tb => (
          <button key={tb.key} onClick={() => { setTab(tb.key); if (tb.key === 'kunden') loadAlleKunden(); }}
            style={{ padding: '8px 20px', borderRadius: 8, border: 'none', fontSize: 14, fontWeight: tab === tb.key ? 600 : 400, background: tab === tb.key ? '#fff' : 'transparent', color: tab === tb.key ? 'var(--text-primary)' : 'var(--text-secondary)', boxShadow: tab === tb.key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none', cursor: 'pointer' }}>
            {tb.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {loading ? <p style={{ color: 'var(--text-tertiary)' }}>{t('finecom.loading')}</p> : (
        <>
          {tab === 'abrechnungen' && !detail && <AbrechnungenTab abrechnungen={abrechnungen} statusBadge={statusBadge} fmtMonat={fmtMonat} openDetail={openDetail} handleApprove={handleApprove} handleSend={handleSend} handleRevert={handleRevert} handleDelete={handleDelete} handleDownloadPdf={handleDownloadPdf} handleSaveNotizen={handleSaveNotizen} busy={busy} t={t} canEdit={canEdit} />}
          {tab === 'abrechnungen' && detail && <DetailView detail={detail} setDetail={(d) => { setDetail(d); if (!d) setMehraufwand(null); }} statusBadge={statusBadge} fmtMonat={fmtMonat} expanded={expanded} toggleExpand={toggleExpand} handleApprove={handleApprove} handleSend={handleSend} handleRevert={handleRevert} handleDownloadPdf={handleDownloadPdf} handleSaveNotizen={handleSaveNotizen} busy={busy} t={t} mehraufwand={mehraufwand} handleResetMehraufwand={handleResetMehraufwand} handleMehraufwandSaved={(id) => handleMehraufwandSaved(id)} showAlert={showAlert} canEdit={canEdit} onBelegeChanged={(belege) => { setDetail(prev => ({ ...prev, zugewiesene_belege: belege })); setAbrechnungen(prev => prev.map(a => a.id === detail.id ? { ...a, zugewiesene_belege: belege } : a)); }} />}
          {tab === 'statistiken' && <StatistikenTab t={t} showAlert={showAlert} />}
          {tab === 'partner-marge' && <PartnerMargeTab t={t} />}
          {tab === 'kunden' && <KundenTab kunden={kunden} alleKunden={alleKunden} showAddKunde={showAddKunde} setShowAddKunde={setShowAddKunde} handleAddKunde={handleAddKunde} handleRemoveKunde={handleRemoveKunde} handleSyncKunden={handleSyncKunden} t={t} canEdit={canEdit} />}
          {tab === 'einstellungen' && canSettings && <EinstellungenTab config={config} setConfig={setConfig} revshare={revshare} handleSaveConfig={handleSaveConfig} handleSaveRevshare={handleSaveRevshare} busy={busy} t={t} />}
        </>
      )}

      {/* Generate Modal — Month Picker */}
      {showGenerate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowGenerate(false)}>
          <div style={{ background: 'var(--bg-card,#fff)', borderRadius: 16, padding: 24, width: 420, border: '1px solid var(--border-card,#E5E7EB)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600 }}>{t('finecom.generateTitle')}</h3>
              <button onClick={() => setShowGenerate(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>{t('finecom.abrechnungsmonat')}</label>
              <input type="month" value={genMonat} onChange={e => setGenMonat(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-input,#E5E7EB)', borderRadius: 10, fontSize: 14 }} />
              {existingMonths.has(genMonat) && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#D97706', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertCircle size={14} /> {t('finecom.monatExists')}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowGenerate(false)} style={{ padding: '10px 20px', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 10, background: 'transparent', fontSize: 14, cursor: 'pointer', color: 'var(--text-primary)' }}>{t('finecom.cancel')}</button>
              <button onClick={handleGenerate} disabled={busy || !genMonat} style={{ padding: '10px 20px', background: '#338edc', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: busy || !genMonat ? 0.6 : 1 }}>{busy ? '...' : t('finecom.generate')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        confirmColor={confirmModal.confirmColor}
        icon={confirmModal.icon}
        onConfirm={handleConfirmOk}
        onCancel={handleConfirmCancel}
        t={t}
      />

      {/* Alert Modal */}
      <AlertModal
        open={alertMsg.open}
        title={alertMsg.title}
        message={alertMsg.message}
        onClose={() => setAlertMsg({ open: false, title: '', message: '' })}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  ABRECHNUNGEN TAB
// ═══════════════════════════════════════════════════════════════
function AbrechnungenTab({ abrechnungen, statusBadge, fmtMonat, openDetail, handleApprove, handleSend, handleRevert, handleDelete, handleDownloadPdf, handleSaveNotizen, busy, t, canEdit = true }) {
  if (!abrechnungen.length) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)' }}>{t('finecom.noAbrechnungen')}</div>;
  }

  const computeAbweichung = (a) => {
    const belege = a.zugewiesene_belege;
    if (!belege || !Array.isArray(belege) || !belege.length) return null;
    const active = belege.filter(b => b.payment_state !== 'reversed');
    if (!active.length) return null;
    const reBetrag = active.reduce((s, inv) => s + (Number(inv.amount) || 0), 0);
    const gesamtInklMa = (Number(a.summe_finecom) || 0) + (Number(a.mehraufwand_finecom_anteil) || 0);
    return Math.round((reBetrag - gesamtInklMa) * 100) / 100;
  };

  return (
    <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {[t('finecom.colMonat'), t('finecom.colStatus'), t('finecom.mehraufwand'), `Finecom €`, `WeShip €`, t('finecom.gesamtFinecom'), 'RE', t('finecom.abweichung'), t('finecom.colAktionen')].map((h, i) => (
              <th key={i} style={{ padding: '12px 16px', textAlign: (i >= 3 && i <= 5) ? 'right' : 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-table,#F3F4F6)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {abrechnungen.map(a => {
            const gesamtInklMa = (Number(a.summe_finecom) || 0) + (Number(a.mehraufwand_finecom_anteil) || 0);
            const abw = computeAbweichung(a);
            return (
            <tr key={a.id} style={{ borderBottom: '1px solid var(--border-table,#F3F4F6)' }}>
              <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', cursor: 'pointer' }} onClick={() => openDetail(a.id)}>{fmtMonat(a.monat)}</td>
              <td style={{ padding: '12px 16px' }}>{statusBadge(a.status)}</td>
              <td style={{ padding: '12px 16px' }}>
                {a.mehraufwand_uploaded ? (
                  <span style={{ fontSize: 11, color: '#059669' }}>{fmt(a.mehraufwand_summe_stunden || 0)}h / {fmt(a.mehraufwand_summe_eur || 0)} €</span>
                ) : (
                  <span style={{ fontSize: 11, color: 'var(--text-muted,#9CA3AF)' }}>–</span>
                )}
              </td>
              <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, color: 'var(--text-primary)' }}>{fmt(a.summe_finecom)}</td>
              <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, color: 'var(--text-primary)' }}>{fmt(a.summe_weship)}</td>
              <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{fmt(gesamtInklMa)}</td>
              <td style={{ padding: '12px 16px' }}><VendorInvoiceBadge invoices={a.zugewiesene_belege} t={t} /></td>
              <td style={{ padding: '12px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {abw === null ? (
                    <span style={{ fontSize: 12, color: 'var(--text-muted,#9CA3AF)' }}>—</span>
                  ) : Math.abs(abw) < 0.01 ? (
                    <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 500, background: '#D1FAE5', color: '#059669' }}>✓ {fmt(0)}&nbsp;€</span>
                  ) : (
                    <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 500, background: '#FEE2E2', color: '#EF4444' }}>{abw > 0 ? '+' : ''}{fmt(abw)}&nbsp;€</span>
                  )}
                  {a.notizen && <span title={a.notizen} style={{ cursor: 'help', color: 'var(--text-tertiary)' }}><MessageSquare size={14} /></span>}
                </div>
              </td>
              <td style={{ padding: '12px 16px' }}>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => openDetail(a.id)} title={t('finecom.view')} style={iconBtn}><FileText size={16} /></button>
                  <button onClick={() => handleDownloadPdf(a.id)} title="PDF" style={iconBtn}><Receipt size={16} /></button>
                  {canEdit && a.status === 'draft' && <button onClick={() => handleApprove(a.id)} disabled={busy} title={t('finecom.approve')} style={{ ...iconBtn, color: '#338edc' }}><Check size={16} /></button>}
                  {canEdit && a.status === 'approved' && <button onClick={() => handleSend(a.id)} disabled={busy} title={t('finecom.send')} style={{ ...iconBtn, color: '#059669' }}><Send size={16} /></button>}
                  {canEdit && a.status === 'approved' && <button onClick={() => handleRevert(a.id)} disabled={busy} title={t('finecom.revert')} style={{ ...iconBtn, color: '#D97706' }}><RotateCcw size={16} /></button>}
                  {canEdit && <button onClick={() => handleDelete(a.id, fmtMonat(a.monat))} title={t('finecom.delete')} style={{ ...iconBtn, color: '#EF4444' }}><Trash2 size={16} /></button>}
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const iconBtn = { background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 8, color: 'var(--text-secondary)' };

// ═══════════════════════════════════════════════════════════════
//  DETAIL VIEW
// ═══════════════════════════════════════════════════════════════
function DetailView({ detail, setDetail, statusBadge, fmtMonat, expanded, toggleExpand, handleApprove, handleSend, handleRevert, handleDownloadPdf, handleSaveNotizen, busy, t, mehraufwand, handleResetMehraufwand, handleMehraufwandSaved, showAlert, onBelegeChanged, canEdit = true }) {
  const daten = detail.daten || [];
  const [notizenLocal, setNotizenLocal] = useState(detail.notizen || '');
  const notizenDirty = notizenLocal !== (detail.notizen || '');

  // Sync local notizen when detail changes
  useEffect(() => { setNotizenLocal(detail.notizen || ''); }, [detail.notizen]);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setDetail(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text-secondary)' }}>← {t('finecom.back')}</button>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)' }}>{fmtMonat(detail.monat)}</h2>
          {statusBadge(detail.status)}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => handleDownloadPdf(detail.id)} style={ghostBtn}><Receipt size={16} /> PDF</button>
          {canEdit && detail.status === 'draft' && (
            <button onClick={() => handleApprove(detail.id)} disabled={busy || !detail.mehraufwand_uploaded} title={!detail.mehraufwand_uploaded ? t('finecom.mehraufwandNotUploaded') : ''} style={{ ...primaryBtn, opacity: !detail.mehraufwand_uploaded ? 0.5 : 1 }}>
              <Check size={16} /> {t('finecom.approve')}
            </button>
          )}
          {canEdit && detail.status === 'approved' && <button onClick={() => handleRevert(detail.id)} disabled={busy} style={{ ...ghostBtn, color: '#D97706', borderColor: '#D97706' }}><RotateCcw size={16} /> {t('finecom.revert')}</button>}
          {canEdit && detail.status === 'approved' && <button onClick={() => handleSend(detail.id)} disabled={busy} style={{ ...primaryBtn, background: '#059669' }}><Send size={16} /> {t('finecom.send')}</button>}
        </div>
      </div>

      {/* Summary Bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: t('finecom.totalGesamt'), value: fmt(detail.summe_gesamt) },
          { label: t('finecom.totalFinecom'), value: fmt(detail.summe_finecom) },
          { label: t('finecom.totalWeship'), value: fmt(detail.summe_weship) },
        ].map((kpi, i) => (
          <div key={i} style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-secondary)', marginBottom: 8 }}>{kpi.label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: -0.5 }}>{kpi.value} €</div>
          </div>
        ))}
      </div>

      {/* Belege-Zuweisung (manuell) */}
      {canEdit && <BelegeZuweisung detail={detail} t={t} onBelegeChanged={onBelegeChanged} />}

      {/* Mehraufwand Section */}
      <MehraufwandSection detail={detail} mehraufwand={mehraufwand} handleResetMehraufwand={handleResetMehraufwand} handleMehraufwandSaved={handleMehraufwandSaved} showAlert={showAlert} busy={busy} t={t} canEdit={canEdit} />

      {/* Notizen */}
      <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 20, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <MessageSquare size={16} style={{ color: 'var(--text-secondary)' }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('finecom.notizen')}</span>
        </div>
        <textarea
          value={notizenLocal}
          onChange={e => setNotizenLocal(e.target.value)}
          onBlur={() => { if (canEdit && notizenDirty) handleSaveNotizen(detail.id, notizenLocal); }}
          placeholder={t('finecom.notizenPlaceholder')}
          rows={3}
          readOnly={!canEdit}
          style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-input,#E5E7EB)', borderRadius: 10, fontSize: 14, resize: 'vertical', fontFamily: 'inherit', color: 'var(--text-primary)', background: canEdit ? 'var(--bg-input,#fff)' : 'var(--bg-inset,#F8F9FC)' }}
        />
        {canEdit && notizenDirty && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button onClick={() => handleSaveNotizen(detail.id, notizenLocal)} style={{ padding: '6px 16px', background: '#338edc', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>{t('finecom.save') || 'Speichern'}</button>
          </div>
        )}
      </div>

      {/* Per-Customer Cards */}
      {daten.map((k) => (
        <div key={k.kunde_id} style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, marginBottom: 12, overflow: 'hidden' }}>
          {/* Card Header */}
          <div onClick={() => toggleExpand(k.kunde_id)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', cursor: 'pointer' }}>
            <div>
              <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{k.kunde_name}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>FC: {fmt(k.summe_finecom)} € | WS: {fmt(k.summe_weship)} €</span>
              <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{fmt(k.summe_gesamt)} €</span>
              {expanded[k.kunde_id] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </div>
          </div>

          {/* Expanded Detail */}
          {expanded[k.kunde_id] && (
            <div style={{ padding: '0 20px 20px' }}>
              {/* Handling Table */}
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>{t('finecom.outboundHandling')}</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                <thead>
                  <tr>
                    {[t('finecom.colLeistung'), t('finecom.colAnzahl'), t('finecom.colEinzel'), t('finecom.colGesamt'), 'FC %', 'FC €', 'WS €'].map((h, i) => (
                      <th key={i} style={{ padding: '8px 10px', textAlign: i >= 1 ? 'right' : 'left', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-table,#F3F4F6)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {k.handling.map((h, i) => {
                    const dim = h.anzahl === 0;
                    const rc = dim ? 'var(--text-muted,#9CA3AF)' : 'var(--text-primary)';
                    const rc2 = dim ? 'var(--text-muted,#9CA3AF)' : 'var(--text-secondary)';
                    return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border-table,#F3F4F6)', opacity: dim ? 0.5 : 1 }}>
                      <td style={{ padding: '8px 10px', fontSize: 13, color: rc }}>{h.beschreibung}</td>
                      <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right', color: rc }}>{h.anzahl.toLocaleString('de-AT')}</td>
                      <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right', color: rc2 }}>{Number(h.einzelpreis).toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</td>
                      <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right', fontWeight: 500, color: rc }}>{fmt(h.gesamt)}</td>
                      <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right', color: rc2 }}>{Math.round(h.finecom_anteil * 100)}%</td>
                      <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right', color: rc }}>{fmt(h.finecom_betrag)}</td>
                      <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right', color: rc }}>{fmt(h.weship_betrag)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Lager */}
              {k.lager && k.lager.gesamt > 0 && (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {t('finecom.lagervolumen')}
                    {k.lager.aus_drafts && (
                      <span title={t('finecom.lagerDraftsHintLong') || 'Werte basieren auf nicht-gebuchten Odoo-Rechnungen (Drafts) und können sich ändern.'}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: '#92400E', background: '#FEF3C7', padding: '2px 8px', borderRadius: 999, border: '1px solid #FCD34D' }}>
                        <AlertTriangle size={11} /> {t('finecom.lagerDraftsBadge') || 'Vorläufig'}
                      </span>
                    )}
                  </div>
                  <div style={{ padding: '10px 12px', background: k.lager.aus_drafts ? '#FFFBEB' : 'var(--bg-inset,#F8F9FC)', borderRadius: 10, marginBottom: 16, fontSize: 13, color: 'var(--text-primary)', border: k.lager.aus_drafts ? '1px dashed #FCD34D' : '1px solid transparent' }}>
                    {k.lager.volumen_m3} m³ (Ø {k.lager.avg_daily_m3} m³/{t('finecom.day')}, {k.lager.tage} {t('finecom.days')}) — {fmt(k.lager.gesamt)} € — FC {Math.round(k.lager.finecom_anteil * 100)}%: {fmt(k.lager.finecom_betrag)} € | WS: {fmt(k.lager.weship_betrag)} €
                  </div>
                </>
              )}

              {/* Inbound / Retouren Table */}
              {k.inbound && k.inbound.length > 0 ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>{t('finecom.inboundRetouren')}</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                    <thead>
                      <tr>
                        {[t('finecom.colLeistung'), t('finecom.colAnzahl'), t('finecom.colEinzel'), t('finecom.colGesamt'), 'FC %', 'FC €', 'WS €'].map((h, i) => (
                          <th key={i} style={{ padding: '8px 10px', textAlign: i >= 1 ? 'right' : 'left', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-table,#F3F4F6)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {k.inbound.map((h, i) => {
                        const dim = h.anzahl === 0;
                        const rc = dim ? 'var(--text-muted,#9CA3AF)' : 'var(--text-primary)';
                        const rc2 = dim ? 'var(--text-muted,#9CA3AF)' : 'var(--text-secondary)';
                        return (
                        <tr key={i} style={{ borderBottom: '1px solid var(--border-table,#F3F4F6)', opacity: dim ? 0.5 : 1 }}>
                          <td style={{ padding: '8px 10px', fontSize: 13, color: rc }}>{h.beschreibung}</td>
                          <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right', color: rc }}>{h.anzahl.toLocaleString('de-AT')}</td>
                          <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right', color: rc2 }}>{Number(h.einzelpreis).toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</td>
                          <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right', fontWeight: 500, color: rc }}>{fmt(h.gesamt)}</td>
                          <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right', color: rc2 }}>{Math.round(h.finecom_anteil * 100)}%</td>
                          <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right', color: rc }}>{fmt(h.finecom_betrag)}</td>
                          <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right', color: rc }}>{fmt(h.weship_betrag)}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              ) : null}
            </div>
          )}
        </div>
      ))}

      {/* Approved/Sent info */}
      {detail.genehmigt_von_name && (
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 12 }}>
          {t('finecom.approvedBy')}: {detail.genehmigt_von_name} ({new Date(detail.genehmigt_am).toLocaleDateString('de-AT')})
          {detail.gesendet_am && ` — ${t('finecom.sentAt')}: ${new Date(detail.gesendet_am).toLocaleDateString('de-AT')}`}
        </div>
      )}
    </div>
  );
}

const primaryBtn = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#338edc', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const ghostBtn = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'transparent', color: 'var(--text-primary)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 10, fontSize: 13, fontWeight: 500, cursor: 'pointer' };

// ═══════════════════════════════════════════════════════════════
//  STATISTIKEN TAB
// ═══════════════════════════════════════════════════════════════
const CHART_COLORS = ['#338edc', '#22D3EE', '#6366F1', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6', '#EC4899'];

function StatistikenTab({ t, showAlert }) {
  const [zeitraum, setZeitraum] = useState('6M');
  const [uebersicht, setUebersicht] = useState(null);
  const [trend, setTrend] = useState([]);
  const [kundenData, setKundenData] = useState([]);
  const [verteilung, setVerteilung] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backfillBusy, setBackfillBusy] = useState(false);

  const monate = zeitraum === '3M' ? 3 : zeitraum === '6M' ? 6 : zeitraum === '12M' ? 12 : 12;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // First fetch uebersicht to get aktueller_monat for verteilung
      const ub = await api('/api/finecom/statistiken/uebersicht');
      setUebersicht(ub);
      const vtMonat = normalizeMonat(ub?.aktueller_monat?.monat) || new Date().toISOString().substring(0, 10);
      const [tr, ku, vt] = await Promise.all([
        api(`/api/finecom/statistiken/trend?monate=${monate}`),
        api(`/api/finecom/statistiken/kunden?monate=${monate}`),
        api(`/api/finecom/statistiken/verteilung?monat=${vtMonat}`).catch(() => null),
      ]);
      setTrend(tr.monate || []);
      setKundenData(ku.kunden || []);
      setVerteilung(vt);
    } catch (e) { console.error('Statistiken load error:', e); }
    setLoading(false);
  }, [monate]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleBackfill = async () => {
    setBackfillBusy(true);
    try {
      const result = await fetch('/api/finecom/statistiken/backfill', { method: 'POST', credentials: 'include' }).then(r => r.json());
      showAlert(`${result.created || 0} ${t('finecom.statistiken.backfillCreated')}`);
      await loadData();
    } catch (e) { showAlert(t('finecom.statistiken.backfillFailed')); }
    setBackfillBusy(false);
  };

  // Top movers: sort kunden by umsatz_delta_pct
  const sortedByDelta = useMemo(() => {
    return [...kundenData].filter(k => k.trend && k.trend.umsatz_delta_pct !== null && k.trend.umsatz_delta_pct !== undefined).sort((a, b) => (b.trend.umsatz_delta_pct || 0) - (a.trend.umsatz_delta_pct || 0));
  }, [kundenData]);
  const topGainers = sortedByDelta.filter(k => (k.trend.umsatz_delta_pct || 0) > 0).slice(0, 3);
  const topDecliners = sortedByDelta.filter(k => (k.trend.umsatz_delta_pct || 0) < 0).slice(0, 3);

  if (loading) return <p style={{ color: 'var(--text-tertiary)', padding: 20 }}>{t('finecom.loading')}</p>;

  const ub = uebersicht || {};
  const akm = ub.aktueller_monat || {};
  const ytd = ub.ytd || {};

  // Custom tooltip for ComposedChart
  const TrendTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 10, padding: '10px 14px', fontSize: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>{label}</div>
        {payload.map((p, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: p.color }}>
            <span>{p.name}</span>
            <span style={{ fontWeight: 600 }}>{fmt(p.value)} €</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      {/* Zeitraum Toggle + Backfill */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'inline-flex', background: 'var(--bg-inset,#F8F9FC)', borderRadius: 10, padding: 3, border: '1px solid var(--border-card,#E5E7EB)' }}>
          {['3M', '6M', '12M', 'YTD'].map(z => (
            <button key={z} onClick={() => setZeitraum(z)}
              style={{ padding: '6px 16px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: zeitraum === z ? 600 : 400, background: zeitraum === z ? '#fff' : 'transparent', color: zeitraum === z ? 'var(--text-primary)' : 'var(--text-secondary)', boxShadow: zeitraum === z ? '0 1px 3px rgba(0,0,0,0.08)' : 'none', cursor: 'pointer' }}>
              {z}
            </button>
          ))}
        </div>
        <button onClick={handleBackfill} disabled={backfillBusy} style={{ ...ghostBtn, fontSize: 12, padding: '6px 14px', gap: 6 }}>
          <RefreshCw size={14} /> {t('finecom.statistiken.backfill')}
        </button>
      </div>

      {/* KPI Bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: t('finecom.statistiken.ytdGesamt'), value: `${fmt(ytd.gesamt || 0)} €` },
          { label: t('finecom.statistiken.finecomAnteil'), value: `${fmt(ytd.finecom || 0)} €` },
          { label: t('finecom.statistiken.weshipAnteil'), value: `${fmt(ytd.weship || 0)} €` },
          { label: t('finecom.statistiken.avgProPaket'), value: `${fmt(ub.avg_umsatz_pro_paket || 0)} €` },
        ].map((kpi, i) => (
          <div key={i} style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-secondary)', marginBottom: 8 }}>{kpi.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: -0.5 }}>{kpi.value}</div>
            {i === 0 && akm.delta_vormonat_pct !== undefined && akm.delta_vormonat_pct !== null && (
              <div style={{ fontSize: 12, marginTop: 6, color: akm.delta_vormonat_pct >= 0 ? '#059669' : '#DC2626', display: 'flex', alignItems: 'center', gap: 4 }}>
                {akm.delta_vormonat_pct >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                {akm.delta_vormonat_pct >= 0 ? '+' : ''}{Number(akm.delta_vormonat_pct).toFixed(1)}% {t('finecom.statistiken.vsVormonat')}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Chart 1: Umsatz-Trend */}
      <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>{t('finecom.statistiken.umsatzTrend')}</div>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={trend} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card,#E5E7EB)" />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: 'var(--text-tertiary)' }} />
            <YAxis tick={{ fontSize: 12, fill: 'var(--text-tertiary)' }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
            <Tooltip content={<TrendTooltip />} />
            <Bar dataKey="finecom" stackId="a" fill="#338edc" name="Finecom" radius={[0, 0, 0, 0]} />
            <Bar dataKey="weship" stackId="a" fill="#22D3EE" name="WeShip" radius={[4, 4, 0, 0]} />
            <Line type="monotone" dataKey="gesamt" stroke="#6366F1" strokeWidth={2} strokeDasharray="5 5" dot={false} name={t('finecom.statistiken.gesamt')} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Chart 2 + 3: Kunden-Verteilung + RevShare */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Kunden-Anteil Donut */}
        <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>{t('finecom.statistiken.kundenAnteil')}</div>
          {verteilung?.kunden_verteilung?.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={verteilung.kunden_verteilung}
                  dataKey="betrag"
                  nameKey="kunde_name"
                  cx="50%" cy="50%"
                  innerRadius={60} outerRadius={100}
                  paddingAngle={2}
                >
                  {verteilung.kunden_verteilung.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${fmt(value)} €`} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>{t('finecom.statistiken.noData')}</div>
          )}
          {/* Legend */}
          {verteilung?.kunden_verteilung?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
              {verteilung.kunden_verteilung.map((k, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-secondary)' }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: CHART_COLORS[i % CHART_COLORS.length], flexShrink: 0 }} />
                  {k.kunde_name} ({Number(k.anteil_pct).toFixed(0)}%)
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RevShare-Entwicklung */}
        <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>{t('finecom.statistiken.revshareEntwicklung')}</div>
          {verteilung?.revshare_effektiv && (
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 12 }}>
              {t('finecom.statistiken.effektiv')}: FC {Number(verteilung.revshare_effektiv.finecom_pct).toFixed(1)}% / WS {Number(verteilung.revshare_effektiv.weship_pct).toFixed(1)}%
            </div>
          )}
          {trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={trend} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card,#E5E7EB)" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: 'var(--text-tertiary)' }} />
                <YAxis tick={{ fontSize: 12, fill: 'var(--text-tertiary)' }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <Tooltip content={<TrendTooltip />} />
                <Area type="monotone" dataKey="finecom" stackId="1" fill="#338edc" fillOpacity={0.3} stroke="#338edc" name="Finecom" />
                <Area type="monotone" dataKey="weship" stackId="1" fill="#22D3EE" fillOpacity={0.3} stroke="#22D3EE" name="WeShip" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>{t('finecom.statistiken.noData')}</div>
          )}
        </div>
      </div>

      {/* Top Mover Card */}
      {(topGainers.length > 0 || topDecliners.length > 0) && (
        <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 20, marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>{t('finecom.statistiken.topMover')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* Gainers */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#059669', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <TrendingUp size={14} /> {t('finecom.statistiken.zuwaechse')}
              </div>
              {topGainers.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>–</div>
              ) : topGainers.map((k, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < topGainers.length - 1 ? '1px solid var(--border-table,#F3F4F6)' : 'none' }}>
                  <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{k.kunde_name}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#059669' }}>+{Number(k.trend.umsatz_delta_pct).toFixed(1)}%</span>
                </div>
              ))}
            </div>
            {/* Decliners */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#DC2626', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <TrendingDown size={14} /> {t('finecom.statistiken.rueckgaenge')}
              </div>
              {topDecliners.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>–</div>
              ) : topDecliners.map((k, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < topDecliners.length - 1 ? '1px solid var(--border-table,#F3F4F6)' : 'none' }}>
                  <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{k.kunde_name}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#DC2626' }}>{Number(k.trend.umsatz_delta_pct).toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Kunden-Entwicklung Table */}
      <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-table,#F3F4F6)' }}>
          {t('finecom.statistiken.kundenEntwicklung')}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {[t('finecom.colKunde'), t('finecom.statistiken.letzterMonat'), t('finecom.statistiken.vormonat'), 'Delta', 'Orders Δ', t('finecom.statistiken.sparkline')].map((h, i) => (
                <th key={i} style={{ padding: '10px 16px', textAlign: i >= 1 && i <= 4 ? 'right' : 'left', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-table,#F3F4F6)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {kundenData.map((k, idx) => {
              const monateArr = k.monate || [];
              const last = monateArr.length > 0 ? monateArr[monateArr.length - 1] : null;
              const prev = monateArr.length > 1 ? monateArr[monateArr.length - 2] : null;
              const deltaPct = k.trend?.umsatz_delta_pct;
              const ordersDeltaPct = k.trend?.orders_delta_pct;
              // Sparkline data
              const sparkData = monateArr.map(m => ({ v: Number(m.gesamt) || 0 }));

              return (
                <tr key={idx} style={{ borderBottom: '1px solid var(--border-table,#F3F4F6)' }}>
                  <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{k.kunde_name}</td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{last ? `${fmt(last.gesamt)} €` : '–'}</td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', fontSize: 13, color: 'var(--text-secondary)' }}>{prev ? `${fmt(prev.gesamt)} €` : '–'}</td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: deltaPct > 0 ? '#059669' : deltaPct < 0 ? '#DC2626' : 'var(--text-secondary)' }}>
                    {deltaPct !== null && deltaPct !== undefined ? `${deltaPct > 0 ? '+' : ''}${Number(deltaPct).toFixed(1)}%` : '–'}
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', fontSize: 13, color: ordersDeltaPct > 0 ? '#059669' : ordersDeltaPct < 0 ? '#DC2626' : 'var(--text-secondary)' }}>
                    {ordersDeltaPct !== null && ordersDeltaPct !== undefined ? `${ordersDeltaPct > 0 ? '+' : ''}${Number(ordersDeltaPct).toFixed(1)}%` : '–'}
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    {sparkData.length >= 2 ? (
                      <ResponsiveContainer width={80} height={24}>
                        <LineChart data={sparkData}>
                          <Line type="monotone" dataKey="v" stroke="#338edc" strokeWidth={1.5} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : <span style={{ color: 'var(--text-muted)' }}>–</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {kundenData.length === 0 && (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
            {t('finecom.statistiken.noData')} — <button onClick={handleBackfill} disabled={backfillBusy} style={{ background: 'none', border: 'none', color: '#338edc', cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}>{t('finecom.statistiken.backfillHint')}</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  KUNDEN TAB
// ═══════════════════════════════════════════════════════════════
function KundenTab({ kunden, alleKunden, showAddKunde, setShowAddKunde, handleAddKunde, handleRemoveKunde, handleSyncKunden, t, canEdit = true }) {
  const [kundenSearch, setKundenSearch] = useState('');
  const assigned = new Set(kunden.map(k => k.kunde_id));
  const available = alleKunden.filter(k => !assigned.has(k.id));
  const filtered = kundenSearch.length >= 2
    ? available.filter(k => k.name?.toLowerCase().includes(kundenSearch.toLowerCase()) || String(k.id).includes(kundenSearch))
    : available;
  const shown = filtered.slice(0, 20);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{kunden.filter(k => k.aktiv).length} {t('finecom.kundenAssigned')}</div>
        {canEdit && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleSyncKunden} style={{ ...primaryBtn, background: 'var(--bg-card,#fff)', color: 'var(--text-primary)', border: '1px solid var(--border-card,#E5E7EB)' }}><RefreshCw size={16} /> {t('finecom.syncKunden')}</button>
            <button onClick={() => { setShowAddKunde(true); setKundenSearch(''); }} style={primaryBtn}><UserPlus size={16} /> {t('finecom.addKunde')}</button>
          </div>
        )}
      </div>

      <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {[t('finecom.colKunde'), 'ID', t('finecom.colStatus'), t('finecom.colHinzugefuegt'), t('finecom.colAktionen')].map((h, i) => (
                <th key={i} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-table,#F3F4F6)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {kunden.map(k => (
              <tr key={k.id} style={{ borderBottom: '1px solid var(--border-table,#F3F4F6)' }}>
                <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{k.kunde_name}</td>
                <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-secondary)' }}>{k.kunde_id}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 500, background: k.aktiv ? '#D1FAE5' : '#F3F4F6', color: k.aktiv ? '#059669' : '#9CA3AF' }}>
                    {k.aktiv ? t('finecom.active') : t('finecom.inactive')}
                  </span>
                </td>
                <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-secondary)' }}>{k.hinzugefuegt_am ? new Date(k.hinzugefuegt_am).toLocaleDateString('de-AT') : '–'}</td>
                <td style={{ padding: '12px 16px' }}>
                  {canEdit && <button onClick={() => handleRemoveKunde(k.kunde_id)} style={{ ...iconBtn, color: '#EF4444' }} title={t('finecom.remove')}><Trash2 size={16} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Kunde Modal */}
      {showAddKunde && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowAddKunde(false)}>
          <div style={{ background: 'var(--bg-card,#fff)', borderRadius: 16, padding: 24, width: 420, maxHeight: '60vh', overflow: 'auto', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600 }}>{t('finecom.addKunde')}</h3>
              <button onClick={() => setShowAddKunde(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <input
              type="text"
              placeholder={t('finecom.searchKunde')}
              value={kundenSearch}
              onChange={e => setKundenSearch(e.target.value)}
              autoFocus
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-input,#E5E7EB)', borderRadius: 10, fontSize: 14, marginBottom: 12 }}
            />
            {available.length === 0 ? (
              <p style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>{t('finecom.noAvailableKunden')}</p>
            ) : kundenSearch.length < 2 ? (
              <p style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>{t('finecom.searchHint')}</p>
            ) : shown.length === 0 ? (
              <p style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>{t('finecom.noSearchResults')}</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {shown.map(k => (
                  <button key={k.id} onClick={() => handleAddKunde(k.id)} style={{ padding: '10px 14px', textAlign: 'left', background: 'none', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 10, cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)' }}>
                    {k.name} <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>#{k.id}</span>
                  </button>
                ))}
                {filtered.length > 20 && <p style={{ color: 'var(--text-tertiary)', fontSize: 12, textAlign: 'center', marginTop: 4 }}>+{filtered.length - 20} {t('finecom.moreResults')}</p>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  EINSTELLUNGEN TAB
// ═══════════════════════════════════════════════════════════════
function AccountMappingCard({ t }) {
  const [mappings, setMappings] = useState([]);
  const [standorte, setStandorte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [status, setStatus] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api('/api/account-mapping'), api('/api/standorte')]).then(([m, s]) => {
      setMappings(m);
      setStandorte(s);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpdate = async (id, field, value) => {
    try {
      await apiPut(`/api/account-mapping/${id}`, { [field]: value });
      setMappings(prev => prev.map(m => m.id === id ? { ...m, [field]: value, standort_name: field === 'standort_id' ? standorte.find(s => s.id === Number(value))?.name : m.standort_name } : m));
    } catch { setStatus({ type: 'error', msg: t('finecom.error') }); }
  };

  const handleDiscover = async () => {
    setDiscovering(true);
    setStatus(null);
    try {
      const res = await apiPost('/api/account-mapping/auto-discover', {});
      if (res.inserted > 0) {
        setStatus({ type: 'success', msg: `${res.inserted} ${t('finecom.accountsDiscovered')}` });
        load();
      } else {
        setStatus({ type: 'info', msg: t('finecom.noNewAccounts') });
      }
    } catch { setStatus({ type: 'error', msg: t('finecom.error') }); }
    setDiscovering(false);
  };

  const shared = mappings.filter(m => m.ist_shared);
  const regular = mappings.filter(m => !m.ist_shared);

  return (
    <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)' }}>{t('finecom.accountMapping')}</div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>{mappings.length} {t('finecom.accountsMapped')}</div>
        </div>
        <button onClick={handleDiscover} disabled={discovering} style={ghostBtn}>
          {discovering ? <RefreshCw size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {t('finecom.autoDiscover')}
        </button>
      </div>
      {status && (
        <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 10, fontSize: 12, fontWeight: 500,
          background: status.type === 'success' ? '#D1FAE5' : status.type === 'info' ? '#EBF4FD' : '#FEE2E2',
          color: status.type === 'success' ? '#059669' : status.type === 'info' ? '#338edc' : '#DC2626' }}>
          {status.msg}
        </div>
      )}
      {loading ? <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-tertiary)' }}>{t('finecom.loading')}</div> : (
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {[t('finecom.colAccount'), t('finecom.colStandort'), t('finecom.colShared')].map((h, i) => (
                  <th key={i} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-table,#F3F4F6)', position: 'sticky', top: 0, background: 'var(--bg-card,#fff)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...shared, ...regular].map(m => (
                <tr key={m.id} style={{ borderBottom: '1px solid var(--border-table,#F3F4F6)' }}>
                  <td style={{ padding: '8px 12px', fontSize: 13, color: 'var(--text-primary)' }}>
                    {m.account_name}
                    {m.beschreibung && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{m.beschreibung}</div>}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <select value={m.standort_id || ''} onChange={e => handleUpdate(m.id, 'standort_id', Number(e.target.value))}
                      style={{ padding: '6px 10px', border: '1px solid var(--border-input,#E5E7EB)', borderRadius: 8, fontSize: 13, background: 'var(--bg-input,#fff)', color: 'var(--text-primary)' }}>
                      {standorte.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    <input type="checkbox" checked={m.ist_shared || false} onChange={e => handleUpdate(m.id, 'ist_shared', e.target.checked)}
                      style={{ width: 16, height: 16, cursor: 'pointer' }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EinstellungenTab({ config, setConfig, revshare, handleSaveConfig, handleSaveRevshare, busy, t }) {
  const updateConfig = (key, val) => setConfig(p => ({ ...p, [key]: val }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Partner-Konfiguration Card */}
      <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 16 }}>{t('finecom.partnerConfig')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, maxWidth: 320 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>{t('finecom.partnerStundensatz')}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="number" step="0.50" min="0" value={typeof config.finecom_stundensatz === 'string' ? config.finecom_stundensatz.replace(/"/g, '') : (config.finecom_stundensatz || 22.5)} onChange={e => updateConfig('finecom_stundensatz', e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-input,#E5E7EB)', borderRadius: 10, fontSize: 14 }} />
              <span style={{ fontSize: 13, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>€/h</span>
            </div>
          </div>
        </div>
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={handleSaveConfig} disabled={busy} style={primaryBtn}>{t('finecom.save')}</button>
        </div>
      </div>

      {/* Account-Mapping Card */}
      <AccountMappingCard t={t} />

      {/* Contact Card */}
      <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}><Settings size={18} /> {t('finecom.contactCard')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {[
            ['finecom_empfaenger_name', t('finecom.recipientName')],
            ['finecom_empfaenger_email', t('finecom.recipientEmail')],
            ['finecom_cc_email', t('finecom.ccEmail')],
            ['finecom_firma', t('finecom.companyName')],
          ].map(([key, label]) => (
            <div key={key}>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>{label}</label>
              <input value={typeof config[key] === 'string' ? config[key].replace(/^"|"$/g, '') : (config[key] || '')} onChange={e => updateConfig(key, e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-input,#E5E7EB)', borderRadius: 10, fontSize: 14 }} />
            </div>
          ))}
        </div>
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={handleSaveConfig} disabled={busy} style={primaryBtn}>{t('finecom.save')}</button>
        </div>
      </div>

      {/* Mehraufwand Settings Card */}
      <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 16 }}>{t('finecom.mehraufwandRevshare')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>{t('finecom.mehraufwandStundensatz')}</label>
            <input type="number" step="1" min="0" value={typeof config.finecom_mehraufwand_stundensatz === 'string' ? config.finecom_mehraufwand_stundensatz.replace(/"/g, '') : (config.finecom_mehraufwand_stundensatz || 45)} onChange={e => updateConfig('finecom_mehraufwand_stundensatz', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-input,#E5E7EB)', borderRadius: 10, fontSize: 14 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Finecom %</label>
            <input type="number" step="0.01" min="0" max="1" value={typeof config.finecom_mehraufwand_revshare_finecom === 'string' ? config.finecom_mehraufwand_revshare_finecom.replace(/"/g, '') : (config.finecom_mehraufwand_revshare_finecom || 0.5)} onChange={e => updateConfig('finecom_mehraufwand_revshare_finecom', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-input,#E5E7EB)', borderRadius: 10, fontSize: 14 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>WeShip %</label>
            <input type="number" step="0.01" min="0" max="1" value={typeof config.finecom_mehraufwand_revshare_weship === 'string' ? config.finecom_mehraufwand_revshare_weship.replace(/"/g, '') : (config.finecom_mehraufwand_revshare_weship || 0.5)} onChange={e => updateConfig('finecom_mehraufwand_revshare_weship', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-input,#E5E7EB)', borderRadius: 10, fontSize: 14 }} />
          </div>
        </div>
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={handleSaveConfig} disabled={busy} style={primaryBtn}>{t('finecom.save')}</button>
        </div>
      </div>

      {/* RevShare Card */}
      <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 16 }}>{t('finecom.revshareTitle')}</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {[t('finecom.colLeistung'), t('finecom.colKategorie'), 'Finecom %', 'WeShip %', ''].map((h, i) => (
                <th key={i} style={{ padding: '8px 12px', textAlign: i >= 2 ? 'right' : 'left', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-table,#F3F4F6)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {revshare.map(rs => (
              <RevShareRow key={rs.id} rs={rs} onSave={handleSaveRevshare} t={t} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VendorInvoiceBadge({ invoices, t }) {
  if (!invoices || invoices.length === 0) {
    return (
      <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 500, background: '#F3F4F6', color: '#6B7280', display: 'inline-block' }}>
        {t('finecom.reNotAssigned') || 'Nicht zugewiesen'}
      </span>
    );
  }

  const active = invoices.filter(inv => inv.state !== 'cancel');
  if (active.length === 0) {
    return (
      <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 500, background: '#F3F4F6', color: '#6B7280', display: 'inline-block' }}>
        {t('finecom.reNotAssigned') || 'Nicht zugewiesen'}
      </span>
    );
  }

  const total = active.reduce((s, inv) => s + (inv.amount || 0), 0);
  const allPaid = active.every(inv => inv.payment_state === 'paid');
  const bg = allPaid ? '#D1FAE5' : '#FEF3C7';
  const color = allPaid ? '#059669' : '#D97706';
  const label = allPaid ? (t('finecom.rePaid') || 'Bezahlt') : (t('finecom.reOpen') || 'Offen');

  const tooltipLines = invoices.map(inv => {
    const status = inv.payment_state === 'paid' ? '✓' : '○';
    return `${status} ${inv.name}: ${fmt(inv.amount)} €`;
  }).join('\n');

  return (
    <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 500, background: bg, color, display: 'inline-block' }} title={tooltipLines}>
      {label} {fmt(total)} €
    </span>
  );
}

// ─── Mehraufwand Section (Upload → Preview/Edit → Confirm) ───
function MehraufwandSection({ detail, mehraufwand, handleResetMehraufwand, handleMehraufwandSaved, showAlert, busy, t, canEdit = true }) {
  const [preview, setPreview] = useState(null); // { entries, sheets, sheetName, autoMatched, usedFallback, filePath, stundensatz }
  const [editEntries, setEditEntries] = useState([]); // editable copy of preview entries
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const maEntries = mehraufwand?.entries || [];
  const maStundensatz = mehraufwand?.stundensatz || 45;

  const handleUpload = async (file, sheet) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const url = `/api/finecom/abrechnungen/${detail.id}/mehraufwand` + (sheet ? `?sheet=${encodeURIComponent(sheet)}` : '');
      const resp = await fetch(url, { method: 'POST', credentials: 'include', body: formData });
      const result = await resp.json();
      if (!resp.ok) { showAlert(result.error || t('finecom.errorUpload')); setUploading(false); return; }
      setPreview(result);
      setEditEntries(result.entries.map((e, i) => ({ ...e, _idx: i })));
    } catch (e) { showAlert(t('finecom.errorUpload')); }
    setUploading(false);
  };

  const handleFileChange = (file) => { if (file) handleUpload(file, null); };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) handleFileChange(file);
  };

  const handleSheetChange = (sheet) => {
    // Re-upload with forced sheet
    if (fileRef.current?.files?.[0]) handleUpload(fileRef.current.files[0], sheet);
  };

  const updateEntry = (idx, field, value) => {
    setEditEntries(prev => prev.map(e => e._idx === idx ? { ...e, [field]: value } : e));
  };

  const removeEntry = (idx) => {
    setEditEntries(prev => prev.filter(e => e._idx !== idx));
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const payload = { entries: editEntries.map(({ _idx, match_type, matched_name, ...rest }) => rest), filePath: preview.filePath };
      const resp = await fetch(`/api/finecom/abrechnungen/${detail.id}/mehraufwand/confirm`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await resp.json();
      if (!resp.ok) { showAlert(result.error || t('finecom.error')); setConfirming(false); return; }
      setPreview(null);
      setEditEntries([]);
      handleMehraufwandSaved(detail.id);
    } catch (e) { showAlert(t('finecom.error')); }
    setConfirming(false);
  };

  const handleCancelPreview = () => { setPreview(null); setEditEntries([]); };

  const totalStunden = editEntries.reduce((s, e) => s + (e.stunden || 0), 0);

  // ─── STATE: PREVIEW MODE ───
  if (preview) {
    return (
      <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{t('finecom.mehraufwand')}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 500, background: '#EBF4FD', color: '#338edc' }}>
              {t('finecom.maPreview') || 'Vorschau'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleCancelPreview} style={{ ...ghostBtn, fontSize: 12, padding: '6px 12px' }}>
              <X size={14} /> {t('finecom.cancel')}
            </button>
            <button onClick={handleConfirm} disabled={confirming || editEntries.length === 0}
              style={{ ...primaryBtn, fontSize: 12, padding: '6px 14px', opacity: confirming || editEntries.length === 0 ? 0.6 : 1 }}>
              <Check size={14} /> {confirming ? '...' : (t('finecom.maConfirm') || 'Übernehmen')}
            </button>
          </div>
        </div>

        {/* Sheet selector + warning */}
        {preview.sheets && preview.sheets.length > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, padding: '8px 12px', borderRadius: 10, background: preview.usedFallback ? '#FEF3C7' : 'var(--bg-inset,#F8F9FC)', border: preview.usedFallback ? '1px solid #FDE68A' : 'none' }}>
            {preview.usedFallback && <AlertTriangle size={14} style={{ color: '#D97706', flexShrink: 0 }} />}
            <span style={{ fontSize: 12, color: preview.usedFallback ? '#92400E' : 'var(--text-secondary)' }}>
              {t('finecom.maSheet') || 'Sheet'}:
            </span>
            <select value={preview.sheetName} onChange={e => handleSheetChange(e.target.value)}
              style={{ padding: '4px 8px', border: '1px solid var(--border-input,#E5E7EB)', borderRadius: 6, fontSize: 12, background: 'var(--bg-card,#fff)' }}>
              {preview.sheets.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {preview.usedFallback && (
              <span style={{ fontSize: 11, color: '#92400E' }}>{t('finecom.maSheetFallback') || 'Kein Monats-Sheet gefunden — bitte manuell wählen'}</span>
            )}
          </div>
        )}

        {/* Summary stats */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
          <div style={{ padding: '6px 12px', background: 'var(--bg-inset,#F8F9FC)', borderRadius: 8, fontSize: 12 }}>
            <span style={{ color: 'var(--text-secondary)' }}>{t('finecom.mehraufwandEntries')}: </span>
            <span style={{ fontWeight: 600 }}>{editEntries.length}</span>
          </div>
          <div style={{ padding: '6px 12px', background: 'var(--bg-inset,#F8F9FC)', borderRadius: 8, fontSize: 12 }}>
            <span style={{ color: 'var(--text-secondary)' }}>{t('finecom.mehraufwandStunden')}: </span>
            <span style={{ fontWeight: 600 }}>{fmt(totalStunden)}h</span>
          </div>
          <div style={{ padding: '6px 12px', background: 'var(--bg-inset,#F8F9FC)', borderRadius: 8, fontSize: 12 }}>
            <span style={{ color: 'var(--text-secondary)' }}>{t('finecom.mehraufwandBetrag')}: </span>
            <span style={{ fontWeight: 600 }}>{fmt(totalStunden * (preview.stundensatz || 45))} €</span>
          </div>
        </div>

        {/* Editable entries table */}
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {[t('finecom.mehraufwandDatum'), t('finecom.mehraufwandKunde'), t('finecom.mehraufwandAufgabe'), t('finecom.mehraufwandStunden'), ''].map((h, i) => (
                  <th key={i} style={{ padding: '6px 8px', textAlign: i === 3 ? 'right' : 'left', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-table,#F3F4F6)', position: 'sticky', top: 0, background: 'var(--bg-card,#fff)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {editEntries.map((e) => (
                <tr key={e._idx} style={{ borderBottom: '1px solid var(--border-table,#F3F4F6)' }}>
                  <td style={{ padding: '6px 8px', fontSize: 12, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                    {e.datum ? new Date(e.datum).toLocaleDateString('de-AT') : '–'}
                  </td>
                  <td style={{ padding: '6px 8px', fontSize: 12, color: 'var(--text-primary)' }}>
                    {e.kunde_name || '–'}
                  </td>
                  <td style={{ padding: '6px 8px', fontSize: 12 }}>
                    <input value={e.aufgabe || ''} onChange={ev => updateEntry(e._idx, 'aufgabe', ev.target.value)}
                      style={{ width: '100%', padding: '3px 6px', border: '1px solid var(--border-input,#E5E7EB)', borderRadius: 6, fontSize: 12, background: 'transparent' }} />
                  </td>
                  <td style={{ padding: '6px 8px', fontSize: 12, textAlign: 'right' }}>
                    <input type="number" step="0.25" value={e.stunden || ''} onChange={ev => updateEntry(e._idx, 'stunden', parseFloat(ev.target.value) || 0)}
                      style={{ width: 60, padding: '3px 6px', border: '1px solid var(--border-input,#E5E7EB)', borderRadius: 6, fontSize: 12, textAlign: 'right', background: 'transparent' }} />
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                    <button onClick={() => removeEntry(e._idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#EF4444' }}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ─── STATE: SAVED (uploaded) ───
  if (detail.mehraufwand_uploaded) {
    return (
      <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{t('finecom.mehraufwand')}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 500, background: '#D1FAE5', color: '#059669' }}>
              <CheckCircle size={13} /> {t('finecom.mehraufwandUploaded')}
            </span>
          </div>
          {canEdit && detail.status === 'draft' && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => handleResetMehraufwand(detail.id)} disabled={busy} style={{ ...ghostBtn, color: '#EF4444', borderColor: '#FCA5A5', fontSize: 12, padding: '6px 12px' }}>
                <Trash2 size={14} /> {t('finecom.mehraufwandReset')}
              </button>
              <label style={{ ...primaryBtn, fontSize: 12, padding: '6px 14px', cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
                <Upload size={14} /> {t('finecom.maReupload') || 'Neu hochladen'}
                <input type="file" accept=".xlsx,.xls" ref={fileRef} hidden onChange={e => { if (e.target.files[0]) handleFileChange(e.target.files[0]); e.target.value = ''; }} />
              </label>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 16, marginBottom: maEntries.length > 0 ? 12 : 0 }}>
          <div style={{ padding: '8px 14px', background: 'var(--bg-inset,#F8F9FC)', borderRadius: 10, fontSize: 13 }}>
            <span style={{ color: 'var(--text-secondary)' }}>{t('finecom.mehraufwandStunden')}: </span>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{fmt(detail.mehraufwand_summe_stunden || 0)}h</span>
          </div>
          <div style={{ padding: '8px 14px', background: 'var(--bg-inset,#F8F9FC)', borderRadius: 10, fontSize: 13 }}>
            <span style={{ color: 'var(--text-secondary)' }}>{t('finecom.mehraufwandBetrag')}: </span>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{fmt(detail.mehraufwand_summe_eur || 0)} €</span>
          </div>
          <div style={{ padding: '8px 14px', background: 'var(--bg-inset,#F8F9FC)', borderRadius: 10, fontSize: 13 }}>
            <span style={{ color: 'var(--text-secondary)' }}>{t('finecom.mehraufwandFinecomAnteil')}: </span>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{fmt(Math.round((detail.mehraufwand_summe_eur || 0) * (mehraufwand?.finecom_anteil || 0.5) * 100) / 100)} €</span>
          </div>
          <div style={{ padding: '8px 14px', background: 'var(--bg-inset,#F8F9FC)', borderRadius: 10, fontSize: 13 }}>
            <span style={{ color: 'var(--text-secondary)' }}>{t('finecom.mehraufwandEntries')}: </span>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{maEntries.length}</span>
          </div>
        </div>

        {maEntries.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {[t('finecom.mehraufwandDatum'), t('finecom.mehraufwandKunde'), t('finecom.mehraufwandAufgabe'), t('finecom.mehraufwandStunden'), t('finecom.mehraufwandBetrag')].map((h, i) => (
                  <th key={i} style={{ padding: '8px 10px', textAlign: i >= 3 ? 'right' : 'left', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-table,#F3F4F6)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {maEntries.map((e, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border-table,#F3F4F6)' }}>
                  <td style={{ padding: '8px 10px', fontSize: 13, color: 'var(--text-primary)' }}>{e.datum ? new Date(e.datum).toLocaleDateString('de-AT') : '–'}</td>
                  <td style={{ padding: '8px 10px', fontSize: 13, color: 'var(--text-primary)' }}>{e.kunde_name || '–'}</td>
                  <td style={{ padding: '8px 10px', fontSize: 13, color: 'var(--text-secondary)' }}>{e.aufgabe || '–'}</td>
                  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right', color: 'var(--text-primary)' }}>{fmt(e.stunden)}</td>
                  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right', fontWeight: 500, color: 'var(--text-primary)' }}>{fmt(e.stunden * maStundensatz)} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  // ─── STATE: EMPTY (no upload yet) ───
  return (
    <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{t('finecom.mehraufwand')}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 500, background: '#FEF3C7', color: '#D97706' }}>
          <AlertCircle size={13} /> {t('finecom.mehraufwandNotUploaded')}
        </span>
      </div>
      {canEdit && detail.status === 'draft' ? (
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          style={{ border: `2px dashed ${dragOver ? '#338edc' : 'var(--border-card,#E5E7EB)'}`, borderRadius: 12, padding: '32px 20px', textAlign: 'center', background: dragOver ? '#EBF4FD' : 'var(--bg-inset,#F8F9FC)', transition: 'all 0.15s' }}>
          <Upload size={24} style={{ color: 'var(--text-tertiary)', marginBottom: 8 }} />
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
            {t('finecom.maDropHint') || 'Excel-Datei hierher ziehen oder klicken'}
          </div>
          <label style={{ ...primaryBtn, fontSize: 12, padding: '6px 14px', cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.6 : 1 }}>
            <Upload size={14} /> {uploading ? '...' : (t('finecom.mehraufwandUpload'))}
            <input type="file" accept=".xlsx,.xls" ref={fileRef} hidden onChange={e => { if (e.target.files[0]) handleFileChange(e.target.files[0]); e.target.value = ''; }} />
          </label>
        </div>
      ) : (
        <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>{t('finecom.maNoData') || 'Kein Mehraufwand hochgeladen.'}</div>
      )}
    </div>
  );
}

// ─── Belege-Zuweisung (manuelle Zuordnung von Odoo-Belegnummern) ───
function BelegeZuweisung({ detail, t, onBelegeChanged }) {
  const [input, setInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [removing, setRemoving] = useState(null); // name of beleg being removed

  const belege = detail.zugewiesene_belege || [];

  const handleSearch = async () => {
    const nums = input.split(/[,;\n]+/).map(s => s.trim()).filter(Boolean);
    if (nums.length === 0) return;
    setSearching(true);
    setSearchResults(null);
    try {
      const res = await apiPost(`/api/finecom/abrechnungen/${detail.id}/belege/suchen`, { belegnummern: nums });
      setSearchResults(res);
    } catch (e) {
      setSearchResults({ found: [], notFound: nums, error: e.error || e.message || 'Fehler' });
    }
    setSearching(false);
  };

  const handleAssign = async () => {
    if (!searchResults?.found?.length) return;
    const allNums = [...belege.map(b => b.name), ...searchResults.found.map(f => f.name)];
    const unique = [...new Set(allNums)];
    setAssigning(true);
    try {
      const res = await apiPut(`/api/finecom/abrechnungen/${detail.id}/belege`, { belegnummern: unique });
      onBelegeChanged?.(res.belege);
      setInput('');
      setSearchResults(null);
    } catch (e) {
      console.error(e);
    }
    setAssigning(false);
  };

  const handleRemove = async (name) => {
    const remaining = belege.filter(b => b.name !== name).map(b => b.name);
    setRemoving(name);
    try {
      const res = await apiPut(`/api/finecom/abrechnungen/${detail.id}/belege`, { belegnummern: remaining });
      onBelegeChanged?.(res.belege);
    } catch (e) { console.error(e); }
    setRemoving(null);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await api(`/api/finecom/abrechnungen/${detail.id}/belege/refresh`);
      onBelegeChanged?.(res.belege);
    } catch (e) { console.error(e); }
    setRefreshing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleSearch(); }
  };

  return (
    <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-secondary)' }}>
          {t('finecom.belegeTitle') || 'Belege-Zuweisung'}
        </div>
        {belege.length > 0 && (
          <button onClick={handleRefresh} disabled={refreshing} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', border: '1px solid var(--border-input,#E5E7EB)', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text-secondary)' }}>
            <RefreshCw size={13} className={refreshing ? 'spin' : ''} /> {t('finecom.belegeRefresh') || 'Status aktualisieren'}
          </button>
        )}
      </div>

      {/* Assigned invoices */}
      {belege.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
          {belege.map((inv, i) => {
            const isPaid = inv.payment_state === 'paid';
            const isRev = inv.payment_state === 'reversed';
            return (
              <div key={i} style={{ padding: '8px 14px', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 10, fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{inv.name}</span>
                  <button onClick={() => handleRemove(inv.name)} disabled={removing !== null} style={{ background: 'none', border: 'none', cursor: removing !== null ? 'wait' : 'pointer', padding: 2, color: 'var(--text-muted,#9CA3AF)', opacity: removing === inv.name ? 0.4 : 1 }} title={t('finecom.remove')}><X size={14} /></button>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{inv.partner} — {inv.date}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                  <span style={{ fontWeight: 600 }}>{fmt(inv.amount)} €</span>
                  <span style={{ padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 500,
                    background: isPaid ? '#D1FAE5' : isRev ? '#FEE2E2' : '#FEF3C7',
                    color: isPaid ? '#059669' : isRev ? '#DC2626' : '#D97706' }}>
                    {isPaid ? t('finecom.rePaid') : isRev ? t('finecom.reReversed') : t('finecom.reOpen')}
                  </span>
                  {inv.ref && <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Ref: {inv.ref}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Input for new bill numbers */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <input
          type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
          placeholder={t('finecom.belegePlaceholder') || 'BILL/2026/0042, BILL/2026/0043'}
          style={{ flex: 1, padding: '8px 12px', border: '1px solid var(--border-input,#E5E7EB)', borderRadius: 8, fontSize: 13 }}
        />
        <button onClick={handleSearch} disabled={searching || !input.trim()} style={{ padding: '8px 16px', border: '1px solid var(--border-input,#E5E7EB)', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
          {searching ? '...' : (t('finecom.belegeSuchen') || 'Suchen')}
        </button>
      </div>

      {/* Search results */}
      {searchResults && (
        <div style={{ marginTop: 10 }}>
          {searchResults.error && (
            <div style={{ padding: '8px 12px', background: '#FEE2E2', borderRadius: 8, fontSize: 12, color: '#DC2626', marginBottom: 8 }}>{searchResults.error}</div>
          )}
          {searchResults.notFound?.length > 0 && (
            <div style={{ padding: '8px 12px', background: '#FEF3C7', borderRadius: 8, fontSize: 12, color: '#D97706', marginBottom: 8 }}>
              {t('finecom.belegeNotFound') || 'Nicht gefunden:'} {searchResults.notFound.join(', ')}
            </div>
          )}
          {searchResults.found?.length > 0 && (
            <div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                {searchResults.found.map((inv, i) => (
                  <div key={i} style={{ padding: '6px 12px', border: '1px solid #D1FAE5', borderRadius: 8, fontSize: 12, background: '#F0FDF4' }}>
                    <span style={{ fontWeight: 600 }}>{inv.name}</span> — {fmt(inv.amount)} € — {inv.payment_state === 'paid' ? t('finecom.rePaid') : t('finecom.reOpen')}
                    <span style={{ marginLeft: 6, color: 'var(--text-tertiary)' }}>{inv.partner}</span>
                  </div>
                ))}
              </div>
              <button onClick={handleAssign} disabled={assigning} style={{ padding: '8px 20px', background: '#338edc', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                {assigning ? '...' : (t('finecom.belegeZuweisen') || 'Zuweisen')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RevShareRow({ rs, onSave, t }) {
  const [fc, setFc] = useState(Number(rs.finecom_anteil));
  const [ws, setWs] = useState(Number(rs.weship_anteil));
  const changed = fc !== Number(rs.finecom_anteil) || ws !== Number(rs.weship_anteil);

  return (
    <tr style={{ borderBottom: '1px solid var(--border-table,#F3F4F6)' }}>
      <td style={{ padding: '8px 12px', fontSize: 13, color: 'var(--text-primary)' }}>{rs.beschreibung}</td>
      <td style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-secondary)' }}>{rs.kategorie}</td>
      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
        <input type="number" step="0.01" min="0" max="1" value={fc} onChange={e => { setFc(Number(e.target.value)); setWs(Math.round((1 - Number(e.target.value)) * 10000) / 10000); }}
          style={{ width: 70, padding: '4px 8px', textAlign: 'right', border: '1px solid var(--border-input,#E5E7EB)', borderRadius: 6, fontSize: 13 }} />
      </td>
      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
        <input type="number" step="0.01" min="0" max="1" value={ws} onChange={e => { setWs(Number(e.target.value)); setFc(Math.round((1 - Number(e.target.value)) * 10000) / 10000); }}
          style={{ width: 70, padding: '4px 8px', textAlign: 'right', border: '1px solid var(--border-input,#E5E7EB)', borderRadius: 6, fontSize: 13 }} />
      </td>
      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
        {changed && <button onClick={() => onSave(rs.id, fc, ws)} style={{ padding: '4px 12px', background: '#338edc', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>{t('finecom.save')}</button>}
      </td>
    </tr>
  );
}

// ═══════════════════════════════════════════════════════════════
//  PARTNER-MARGE TAB
// ═══════════════════════════════════════════════════════════════
function PartnerMargeTab({ t }) {
  // Build last 12 months options
  const monthOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let i = 1; i <= 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      opts.push({ monat: d.getMonth() + 1, jahr: d.getFullYear(), label: d.toLocaleDateString('de-AT', { month: 'long', year: 'numeric' }) });
    }
    return opts;
  }, []);

  const [selectedIdx, setSelectedIdx] = useState(0);
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortCol, setSortCol] = useState('marge_prozent');
  const [sortDir, setSortDir] = useState('desc');

  const sel = monthOptions[selectedIdx];

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [margeData, trendData] = await Promise.all([
        api(`/api/finecom/partner-marge?monat=${sel.monat}&jahr=${sel.jahr}`),
        api(`/api/finecom/partner-marge/trend?monate=6`),
      ]);
      setData(margeData);
      setTrend(Array.isArray(trendData) ? trendData : []);
    } catch (e) {
      console.error('Partner-Marge load error:', e);
      setData(null);
      setTrend([]);
    }
    setLoading(false);
  }, [sel.monat, sel.jahr]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const sortedKunden = useMemo(() => {
    if (!data?.kunden) return [];
    return [...data.kunden].sort((a, b) => {
      const av = a[sortCol] ?? 0, bv = b[sortCol] ?? 0;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
  }, [data?.kunden, sortCol, sortDir]);

  // Totals
  const totals = useMemo(() => {
    if (!data?.kunden) return null;
    const k = data.kunden;
    const totalPick = k.reduce((s, r) => s + (r.pick_minuten || 0), 0);
    const totalPack = k.reduce((s, r) => s + (r.pack_minuten || 0), 0);
    const totalProd = k.reduce((s, r) => s + (r.productive_minuten || 0), 0);
    const totalIdle = k.reduce((s, r) => s + (r.idle_minuten || 0), 0);
    const totalAz = k.reduce((s, r) => s + (r.arbeitszeit_minuten || 0), 0);
    const totalStd = k.reduce((s, r) => s + (r.arbeitszeit_stunden || 0), 0);
    const totalPK = k.reduce((s, r) => s + (r.personalkosten || 0), 0);
    const totalRS = k.reduce((s, r) => s + (r.revshare_euro || 0), 0);
    const totalMarge = k.reduce((s, r) => s + (r.marge_euro || 0), 0);
    const totalAuftr = k.reduce((s, r) => s + (r.auftraege || 0), 0);
    const margePct = totalRS > 0 ? Math.round(totalMarge / totalRS * 1000) / 10 : 0;
    return { auftraege: totalAuftr, pick_minuten: totalPick, pack_minuten: totalPack, productive_minuten: totalProd, idle_minuten: totalIdle, arbeitszeit_minuten: totalAz, arbeitszeit_stunden: totalStd, personalkosten: totalPK, revshare_euro: totalRS, marge_euro: totalMarge, marge_prozent: margePct };
  }, [data?.kunden]);

  if (loading) return <p style={{ color: 'var(--text-tertiary)', padding: 20 }}>{t('finecom.loading')}</p>;

  const zf = data?.zusammenfassung;
  const hasData = zf && (zf.revshare_einnahmen > 0 || zf.personalkosten > 0);

  // Empty State
  if (!hasData) {
    return (
      <div>
        {/* Month selector */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
          <select value={selectedIdx} onChange={e => setSelectedIdx(Number(e.target.value))} style={{ padding: '8px 12px', border: '1px solid var(--border-input,#E5E7EB)', borderRadius: 10, fontSize: 14, color: 'var(--text-primary)', background: 'var(--bg-input,#fff)' }}>
            {monthOptions.map((o, i) => <option key={i} value={i}>{o.label}</option>)}
          </select>
        </div>
        <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 48, textAlign: 'center' }}>
          <Info size={40} style={{ color: 'var(--text-tertiary)', margin: '0 auto 16px' }} />
          <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>{t('finecom.partnerMarge.noData')}</h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{t('finecom.partnerMarge.selectEarlier')}</p>
        </div>
      </div>
    );
  }

  const fmtEuro = (n) => Number(n || 0).toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtInt = (n) => Number(n || 0).toLocaleString('de-AT');

  // Trend chart data
  const chartData = trend.map(m => {
    const label = new Date(m.jahr, m.monat - 1).toLocaleDateString('de-AT', { month: 'short' });
    return { name: label, revshare: m.revshare, personalkosten: m.personalkosten, marge_pct: m.marge_prozent };
  });

  const SortHeader = ({ col, label, align }) => (
    <th onClick={() => toggleSort(col)} style={{ padding: '12px 16px', textAlign: align || 'right', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: sortCol === col ? 'var(--text-primary)' : 'var(--text-tertiary)', borderBottom: '1px solid var(--border-table,#F3F4F6)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label}
        {sortCol === col && <ArrowUpDown size={12} style={{ opacity: 0.6 }} />}
      </span>
    </th>
  );

  return (
    <div>
      {/* Month selector */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
        <select value={selectedIdx} onChange={e => setSelectedIdx(Number(e.target.value))} style={{ padding: '8px 12px', border: '1px solid var(--border-input,#E5E7EB)', borderRadius: 10, fontSize: 14, color: 'var(--text-primary)', background: 'var(--bg-input,#fff)' }}>
          {monthOptions.map((o, i) => <option key={i} value={i}>{o.label}</option>)}
        </select>
      </div>

      {/* KPI Bar — 4 Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {/* RevShare */}
        <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-secondary)', marginBottom: 8 }}>{t('finecom.partnerMarge.revshareEinnahmen')}</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: -0.5 }}>€{fmtEuro(zf.revshare_einnahmen)}</div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('finecom.partnerMarge.revshareDesc')}</div>
        </div>
        {/* Personalkosten */}
        <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-secondary)', marginBottom: 8 }}>{t('finecom.partnerMarge.personalkosten')}</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: -0.5 }}>€{fmtEuro(zf.personalkosten)}</div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('finecom.partnerMarge.pkDesc').replace('{{stunden}}', String(zf.gesamt_stunden)).replace('{{stundensatz}}', String(zf.stundensatz))}</div>
          {zf.idle_minuten > 0 && <div style={{ fontSize: 11, color: 'var(--text-muted,#9CA3AF)', marginTop: 2 }}>Idle: {(zf.idle_minuten / 60).toFixed(1)}h (excluded)</div>}
        </div>
        {/* Marge */}
        <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-secondary)', marginBottom: 8 }}>{t('finecom.partnerMarge.marge')}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: -0.5 }}>€{fmtEuro(zf.marge_euro)}</span>
            <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 500, background: zf.marge_euro >= 0 ? '#D1FAE5' : '#FEE2E2', color: zf.marge_euro >= 0 ? '#059669' : '#DC2626' }}>{zf.marge_prozent}%</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>{zf.marge_euro >= 0 ? '' : '⚠ '}{zf.marge_euro >= 0 ? '' : 'Negative Marge'}</div>
        </div>
        {/* Kosten/Auftrag */}
        <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--text-secondary)', marginBottom: 8 }}>{t('finecom.partnerMarge.kostenProAuftrag')}</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: -0.5 }}>€{fmtEuro(zf.kosten_pro_auftrag)}</div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('finecom.partnerMarge.kostenDesc').replace('{{auftraege}}', fmtInt(zf.anzahl_auftraege))}</div>
        </div>
      </div>

      {/* Info Banner */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '12px 16px', background: 'var(--bg-inset,#F8F9FC)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 10, marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        <Info size={16} style={{ flexShrink: 0, marginTop: 2, color: 'var(--text-tertiary)' }} />
        <span>{t('finecom.partnerMarge.idleInfo').replace(/\{\{threshold\}\}/g, String(zf.schwellenwert_sekunden || 30))}</span>
      </div>

      {/* Kunden-Tabelle */}
      <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 24, marginBottom: 24 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <SortHeader col="kunde_name" label={t('finecom.partnerMarge.kunde')} align="left" />
              <SortHeader col="auftraege" label={t('finecom.partnerMarge.auftraege')} />
              <SortHeader col="pick_minuten" label={t('finecom.partnerMarge.pickMin')} />
              <SortHeader col="pack_minuten" label={t('finecom.partnerMarge.packMin')} />
              <SortHeader col="productive_minuten" label={t('finecom.partnerMarge.transition')} />
              <SortHeader col="arbeitszeit_stunden" label={t('finecom.partnerMarge.gesamtStd')} />
              <SortHeader col="idle_minuten" label={t('finecom.partnerMarge.idleMin')} />
              <SortHeader col="personalkosten" label={t('finecom.partnerMarge.pk')} />
              <SortHeader col="revshare_euro" label={t('finecom.partnerMarge.revshare')} />
              <SortHeader col="marge_euro" label={t('finecom.partnerMarge.margeEuro')} />
              <SortHeader col="marge_prozent" label={t('finecom.partnerMarge.margeProzent')} />
            </tr>
          </thead>
          <tbody>
            {sortedKunden.map(k => {
              const avgIdle = data?.kunden?.length ? data.kunden.reduce((s, r) => s + (r.idle_minuten || 0), 0) / data.kunden.length : 0;
              const highIdle = (k.idle_minuten || 0) > avgIdle * 1.5 && k.idle_minuten > 10;
              return (
              <tr key={k.kunde_id} style={{ borderBottom: '1px solid var(--border-table,#F3F4F6)', background: k.marge_euro < 0 ? 'rgba(239,68,68,0.04)' : 'transparent' }}>
                <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{k.kunde_name}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, color: 'var(--text-primary)' }}>{fmtInt(k.auftraege)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, color: 'var(--text-primary)' }}>{(k.pick_minuten || 0).toFixed(1)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, color: 'var(--text-primary)' }}>{(k.pack_minuten || 0).toFixed(1)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, color: 'var(--text-tertiary)' }}>{(k.productive_minuten || 0).toFixed(1)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, color: 'var(--text-primary)' }}>{(k.arbeitszeit_stunden || 0).toFixed(1)}h</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, color: highIdle ? '#DC2626' : 'var(--text-tertiary)' }}>{highIdle ? '\u26A0 ' : ''}{(k.idle_minuten || 0).toFixed(1)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, color: 'var(--text-primary)' }}>€{fmtEuro(k.personalkosten)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, color: 'var(--text-primary)' }}>€{fmtEuro(k.revshare_euro)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, fontWeight: 500, color: k.marge_euro >= 0 ? '#059669' : '#DC2626' }}>€{fmtEuro(k.marge_euro)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, fontWeight: 500, color: k.marge_prozent >= 0 ? '#059669' : '#DC2626' }}>{k.marge_prozent}%</td>
              </tr>
              );
            })}
          </tbody>
          {totals && (
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--border-card,#E5E7EB)' }}>
                <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{t('finecom.partnerMarge.gesamt')}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{fmtInt(totals.auftraege)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{totals.pick_minuten.toFixed(1)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{totals.pack_minuten.toFixed(1)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, fontWeight: 700, color: 'var(--text-tertiary)' }}>{totals.productive_minuten.toFixed(1)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{totals.arbeitszeit_stunden.toFixed(1)}h</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, fontWeight: 700, color: 'var(--text-tertiary)' }}>{totals.idle_minuten.toFixed(1)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>€{fmtEuro(totals.personalkosten)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>€{fmtEuro(totals.revshare_euro)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, fontWeight: 700, color: totals.marge_euro >= 0 ? '#059669' : '#DC2626' }}>€{fmtEuro(totals.marge_euro)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, fontWeight: 700, color: totals.marge_prozent >= 0 ? '#059669' : '#DC2626' }}>{totals.marge_prozent}%</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Trend Chart */}
      {chartData.length > 1 && (
        <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 16, padding: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>{t('finecom.partnerMarge.trend')}</h3>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-table,#F3F4F6)" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: 'var(--text-tertiary)', fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="euro" tick={{ fill: 'var(--text-tertiary)', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={v => `€${(v / 1000).toFixed(0)}k`} />
              <YAxis yAxisId="pct" orientation="right" tick={{ fill: 'var(--text-tertiary)', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} domain={[0, 100]} />
              <Tooltip content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                return (
                  <div style={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 8, padding: '12px 16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>{label}</div>
                    {payload.map((p, i) => (
                      <div key={i} style={{ fontSize: 14, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, display: 'inline-block' }} />
                        {p.name}: {p.name === 'Marge %' ? `${p.value}%` : `€${fmtEuro(p.value)}`}
                      </div>
                    ))}
                  </div>
                );
              }} />
              <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ fontSize: 12, color: 'var(--text-secondary)' }} />
              <Bar yAxisId="euro" dataKey="revshare" name={t('finecom.partnerMarge.revshareEinnahmen')} fill="#338edc" stackId="stack" radius={[0, 0, 0, 0]} />
              <Bar yAxisId="euro" dataKey="personalkosten" name={t('finecom.partnerMarge.personalkosten')} fill="#22D3EE" stackId="stack" radius={[4, 4, 0, 0]} />
              <Line yAxisId="pct" type="monotone" dataKey="marge_pct" name="Marge %" stroke="#6366F1" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4, fill: '#6366F1', stroke: '#fff', strokeWidth: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
